import { Box, Container, Flex, Text, VStack, Skeleton, HStack, Grid, GridItem, Stat, StatLabel, StatNumber, Button } from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { Link } from "../components/Link/Link";
import { ThemeColor } from "../theme/palette";
import { useEffect, useState } from "react";
import { useFutarchyData } from "../hooks/useFutarchyData";

interface GovernancePageProps {
    title: string;
    content?: string; // Markdown content (optional if contentPath is provided)
    contentPath?: string; // Path to markdown file in public folder (e.g., "/governance/kyros-1.md")
    futarchyLink: string; // Link to MetaDAO futarchy page
    endTime?: Date; // End time for the governance proposal
    isPassed?: boolean; // Whether the proposal passed (only relevant if ended)
}

export const GovernancePage = ({ title, content, contentPath, futarchyLink, endTime, isPassed }: GovernancePageProps) => {
    const [markdownContent, setMarkdownContent] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
    const [showCountdown, setShowCountdown] = useState<boolean>(false);
    const [hasEnded, setHasEnded] = useState<boolean>(false);
    const { futarchyData, isLoading: isLoadingFutarchy } = useFutarchyData();

    useEffect(() => {
        if (contentPath) {
            setIsLoading(true);
            setError(null);
            fetch(contentPath)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Failed to load markdown file: ${response.statusText}`);
                    }
                    return response.text();
                })
                .then((text) => {
                    setMarkdownContent(text);
                    setIsLoading(false);
                })
                .catch((err) => {
                    setError(err.message);
                    setIsLoading(false);
                });
        } else if (content) {
            setMarkdownContent(content);
        }
    }, [contentPath, content]);

    useEffect(() => {
        if (endTime) {
            const updateCountdown = () => {
                const now = Date.now();
                const endTimeMs = endTime.getTime();
                const timeRemaining = endTimeMs - now;

                if (timeRemaining <= 0) {
                    setShowCountdown(false);
                    setTimeLeft(null);
                    setHasEnded(true);
                    return;
                }

                const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
                const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

                setTimeLeft({ days, hours, minutes, seconds });
                setShowCountdown(true);
                setHasEnded(false);
            };

            updateCountdown();
            const interval = setInterval(updateCountdown, 1000);

            return () => clearInterval(interval);
        }
    }, [endTime]);

    const displayContent = contentPath ? markdownContent : (content || "");
    return (
        <Flex direction="column" minHeight="100vh">
            <Container maxW="900px" flex="1" py={8}>
                <VStack spacing={8} align="stretch">
                    <VStack spacing={4} align="stretch">
                        <Flex alignItems="center" justifyContent="flex-start">
                            <Text
                                fontWeight="bold"
                                fontSize="2xl"
                                color="white"
                                lineHeight="1.2"
                                mr={2}
                            >
                                {title}
                            </Text>
                        </Flex>
                        {endTime && (
                            <>
                                {hasEnded ? (
                                    <HStack spacing={2} align="center">
                                        <Text fontSize="sm" color="whiteAlpha.700">
                                            Status:
                                        </Text>
                                        <Box
                                            bg={isPassed === true ? "rgba(34, 197, 94, 0.15)" : isPassed === false ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.15)"}
                                            borderRadius="8px"
                                            px={3}
                                            py={1}
                                            border="0.5px solid"
                                            borderColor={isPassed === true ? "green.400" : isPassed === false ? "red.400" : "red.400"}
                                        >
                                            <Text fontSize="sm" fontWeight="bold" color={isPassed === true ? "green.400" : isPassed === false ? "red.400" : "red.400"}>
                                                {isPassed === true ? "Approved" : isPassed === false ? "Rejected" : "Ended"}
                                            </Text>
                                        </Box>
                                    </HStack>
                                ) : showCountdown && timeLeft ? (
                                    <HStack spacing={4} align="center" flexWrap="wrap">
                                        <Text fontSize="md" color="whiteAlpha.800" fontWeight="medium">
                                            Remaining time:
                                        </Text>
                                        {timeLeft.days > 0 && (
                                            <HStack spacing={2} align="center">
                                                <Box
                                                    bg="rgba(95, 244, 244, 0.15)"
                                                    borderRadius="8px"
                                                    px={3}
                                                    py={2}
                                                    border="0.5px solid"
                                                    borderColor={ThemeColor.KAIROS}
                                                    minW="50px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <Text fontSize="lg" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                        {timeLeft.days}
                                                    </Text>
                                                </Box>
                                                <Text fontSize="sm" color="whiteAlpha.700" fontWeight="medium">
                                                    d
                                                </Text>
                                            </HStack>
                                        )}
                                        <HStack spacing={2} align="center">
                                            <Box
                                                bg="rgba(95, 244, 244, 0.15)"
                                                borderRadius="8px"
                                                px={3}
                                                py={2}
                                                border="0.5px solid"
                                                borderColor={ThemeColor.KAIROS}
                                                minW="50px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Text fontSize="lg" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                    {timeLeft.hours.toString().padStart(2, '0')}
                                                </Text>
                                            </Box>
                                            <Text fontSize="sm" color="whiteAlpha.700" fontWeight="medium">
                                                h
                                            </Text>
                                        </HStack>
                                        <HStack spacing={2} align="center">
                                            <Box
                                                bg="rgba(95, 244, 244, 0.15)"
                                                borderRadius="8px"
                                                px={3}
                                                py={2}
                                                border="0.5px solid"
                                                borderColor={ThemeColor.KAIROS}
                                                minW="50px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Text fontSize="lg" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                    {timeLeft.minutes.toString().padStart(2, '0')}
                                                </Text>
                                            </Box>
                                            <Text fontSize="sm" color="whiteAlpha.700" fontWeight="medium">
                                                m
                                            </Text>
                                        </HStack>
                                        <HStack spacing={2} align="center">
                                            <Box
                                                bg="rgba(95, 244, 244, 0.15)"
                                                borderRadius="8px"
                                                px={3}
                                                py={2}
                                                border="0.5px solid"
                                                borderColor={ThemeColor.KAIROS}
                                                minW="50px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Text fontSize="lg" fontWeight="bold" color={ThemeColor.KAIROS}>
                                                    {timeLeft.seconds.toString().padStart(2, '0')}
                                                </Text>
                                            </Box>
                                            <Text fontSize="sm" color="whiteAlpha.700" fontWeight="medium">
                                                s
                                            </Text>
                                        </HStack>
                                    </HStack>
                                ) : null}
                            </>
                        )}
                        {!hasEnded && (
                            <Button
                                as="a"
                                href={futarchyLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                leftIcon={
                                    <Box as="img" src="/metadao.svg" alt="MetaDAO" w="20px" h="20px" />
                                }
                                bg="rgba(95, 244, 244, 0.1)"
                                color={ThemeColor.KAIROS}
                                border="0.5px solid"
                                borderColor={ThemeColor.KAIROS}
                                borderRadius="8px"
                                px={6}
                                py={6}
                                fontSize="md"
                                fontWeight="semibold"
                                _hover={{
                                    bg: "rgba(95, 244, 244, 0.2)",
                                    borderColor: ThemeColor.KAIROS,
                                }}
                                _active={{
                                    bg: "rgba(95, 244, 244, 0.15)",
                                }}
                                w="100%"
                                maxW="300px"
                            >
                                Vote on MetaDAO
                            </Button>
                        )}
                    </VStack>

                    {futarchyData && (
                        <Box
                            p={6}
                            borderRadius="8px"
                            backgroundColor="whiteAlpha.100"
                            border="0.5px solid"
                            borderColor="rgba(95, 244, 244, 0.2)"
                        >
                            <Skeleton isLoaded={!isLoadingFutarchy}>
                                <VStack spacing={4} align="stretch">
                                    <Text fontSize="lg" fontWeight="semibold" color="white" mb={2}>
                                        Statistics
                                    </Text>
                                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                                        <GridItem>
                                            <Box
                                                bg="rgba(95, 244, 244, 0.08)"
                                                borderRadius="8px"
                                                p={4}
                                                border="0.5px solid"
                                                borderColor="rgba(95, 244, 244, 0.2)"
                                            >
                                                <Stat>
                                                    <StatLabel color="whiteAlpha.700" fontSize="sm">
                                                        Total Trades
                                                    </StatLabel>
                                                    <StatNumber color={ThemeColor.KAIROS} fontSize="xl" mt={1}>
                                                        {futarchyData.total_trades.toLocaleString()}
                                                    </StatNumber>
                                                </Stat>
                                            </Box>
                                        </GridItem>
                                        <GridItem>
                                            <Box
                                                bg="rgba(95, 244, 244, 0.08)"
                                                borderRadius="8px"
                                                p={4}
                                                border="0.5px solid"
                                                borderColor="rgba(95, 244, 244, 0.2)"
                                            >
                                                <Stat>
                                                    <StatLabel color="whiteAlpha.700" fontSize="sm">
                                                        Total Volume
                                                    </StatLabel>
                                                    <StatNumber color={ThemeColor.KAIROS} fontSize="xl" mt={1}>
                                                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(futarchyData.total_volume)}
                                                    </StatNumber>
                                                </Stat>
                                            </Box>
                                        </GridItem>
                                        <GridItem>
                                            <Box
                                                bg="rgba(95, 244, 244, 0.08)"
                                                borderRadius="8px"
                                                p={4}
                                                border="0.5px solid"
                                                borderColor="rgba(95, 244, 244, 0.2)"
                                            >
                                                <Stat>
                                                    <StatLabel color="whiteAlpha.700" fontSize="sm">
                                                        Unique Accounts
                                                    </StatLabel>
                                                    <StatNumber color={ThemeColor.KAIROS} fontSize="xl" mt={1}>
                                                        {futarchyData.unique_accounts.toLocaleString()}
                                                    </StatNumber>
                                                </Stat>
                                            </Box>
                                        </GridItem>
                                    </Grid>
                                    {!hasEnded && (
                                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                            <GridItem>
                                                <Box
                                                    bg="rgba(34, 197, 94, 0.15)"
                                                    borderRadius="8px"
                                                    p={4}
                                                    border="0.5px solid"
                                                    borderColor="green.400"
                                                    w="100%"
                                                >
                                                    <Stat>
                                                        <StatLabel color="whiteAlpha.700" fontSize="sm">
                                                            Pass Price
                                                        </StatLabel>
                                                        <StatNumber color="green.400" fontSize="xl" mt={1}>
                                                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 6, maximumFractionDigits: 6 }).format(futarchyData.passPrice)}
                                                        </StatNumber>
                                                    </Stat>
                                                </Box>
                                            </GridItem>
                                            <GridItem>
                                                <Box
                                                    bg="rgba(239, 68, 68, 0.15)"
                                                    borderRadius="8px"
                                                    p={4}
                                                    border="0.5px solid"
                                                    borderColor="red.400"
                                                    w="100%"
                                                >
                                                    <Stat>
                                                        <StatLabel color="whiteAlpha.700" fontSize="sm">
                                                            Fail Price
                                                        </StatLabel>
                                                        <StatNumber color="red.400" fontSize="xl" mt={1}>
                                                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 6, maximumFractionDigits: 6 }).format(futarchyData.failPrice)}
                                                        </StatNumber>
                                                    </Stat>
                                                </Box>
                                            </GridItem>
                                        </Grid>
                                    )}
                                </VStack>
                            </Skeleton>
                        </Box>
                    )}

                    <Box
                        p={6}
                        borderRadius="8px"
                        backgroundColor="whiteAlpha.100"
                        border="0.5px solid"
                        borderColor="rgba(95, 244, 244, 0.2)"
                    >
                        <VStack spacing={6} align="stretch">
                            {isLoading ? (
                                <VStack spacing={4} align="stretch">
                                    <Skeleton height="20px" borderRadius="4px" />
                                    <Skeleton height="20px" borderRadius="4px" />
                                    <Skeleton height="20px" width="80%" borderRadius="4px" />
                                    <Skeleton height="100px" borderRadius="8px" />
                                </VStack>
                            ) : error ? (
                                <Box p={4} borderRadius="8px" backgroundColor="whiteAlpha.200" color="white">
                                    <Text textAlign="center" color="red.400">
                                        Error loading content: {error}
                                    </Text>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        "& p": {
                                            color: "whiteAlpha.800",
                                            fontSize: "md",
                                            lineHeight: "1.6",
                                            marginBottom: "1rem",
                                        },
                                        "& h1": {
                                            color: "white",
                                            fontSize: "xl",
                                            fontWeight: "bold",
                                            marginTop: "1.5rem",
                                            marginBottom: "1rem",
                                        },
                                        "& h2": {
                                            color: "white",
                                            fontSize: "lg",
                                            fontWeight: "bold",
                                            marginTop: "1.5rem",
                                            marginBottom: "1rem",
                                        },
                                        "& h3": {
                                            color: "white",
                                            fontSize: "md",
                                            fontWeight: "semibold",
                                            marginTop: "1.25rem",
                                            marginBottom: "0.75rem",
                                        },
                                        "& ul, & ol": {
                                            color: "whiteAlpha.800",
                                            marginLeft: "1.5rem",
                                            marginBottom: "1rem",
                                        },
                                        "& li": {
                                            marginBottom: "0.5rem",
                                        },
                                        "& a": {
                                            color: ThemeColor.KAIROS,
                                            textDecoration: "underline",
                                            _hover: {
                                                color: ThemeColor.KAIROS,
                                                opacity: 0.8,
                                            },
                                        },
                                        "& code": {
                                            backgroundColor: "whiteAlpha.100",
                                            padding: "0.125rem 0.375rem",
                                            borderRadius: "4px",
                                            fontSize: "0.875rem",
                                            color: ThemeColor.KAIROS,
                                        },
                                        "& pre": {
                                            backgroundColor: "whiteAlpha.100",
                                            padding: "1rem",
                                            borderRadius: "8px",
                                            overflow: "auto",
                                            marginBottom: "1rem",
                                        },
                                        "& pre code": {
                                            backgroundColor: "transparent",
                                            padding: 0,
                                        },
                                        "& blockquote": {
                                            borderLeft: `4px solid ${ThemeColor.KAIROS}`,
                                            paddingLeft: "1rem",
                                            marginLeft: 0,
                                            color: "whiteAlpha.700",
                                            fontStyle: "italic",
                                        },
                                    }}
                                >
                                    <ReactMarkdown>{displayContent}</ReactMarkdown>
                                </Box>
                            )}

                            <Box
                                p={4}
                                borderRadius="8px"
                                backgroundColor="rgba(95, 244, 244, 0.08)"
                                border="0.5px solid"
                                borderColor="rgba(95, 244, 244, 0.2)"
                            >
                                <VStack spacing={3} align="flex-start">
                                    <Text fontSize="md" fontWeight="semibold" color={ThemeColor.KAIROS}>
                                        Participate in Governance
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.6">
                                        View active proposals and participate in futarchy governance on MetaDAO.
                                    </Text>
                                    <Link
                                        to={futarchyLink}
                                        textColor={ThemeColor.KAIROS}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        _hover={{ textDecoration: "underline" }}
                                    >
                                        Go to MetaDAO Futarchy →
                                    </Link>
                                </VStack>
                            </Box>
                        </VStack>
                    </Box>
                </VStack>
            </Container>
        </Flex>
    );
};

